import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Filter, MessageSquare, Users, Hash, Bot } from 'lucide-react';
import type { ChatType } from '../types';

interface ChatFilterOptions {
  chatTypes?: ChatType[];
  unreadOnly?: boolean;
  todayOnly?: boolean;
}

interface ChatFiltersProps {
  filters?: ChatFilterOptions;
  onFiltersChange?: (filters: ChatFilterOptions) => void;
}

const CHAT_TYPES: Array<{ value: ChatType; label: string; icon: React.ReactNode }> = [
  { value: 'user', label: 'Личные чаты', icon: <MessageSquare className="h-4 w-4" /> },
  { value: 'group', label: 'Группы', icon: <Users className="h-4 w-4" /> },
  { value: 'channel', label: 'Каналы', icon: <Hash className="h-4 w-4" /> },
  { value: 'bot', label: 'Боты', icon: <Bot className="h-4 w-4" /> },
];

export function ChatFilters({ filters = {}, onFiltersChange }: ChatFiltersProps) {
  const selectedTypes = filters.chatTypes && filters.chatTypes.length > 0
    ? filters.chatTypes
    : ['user'];
  const unreadOnly = filters.unreadOnly ?? false;
  const todayOnly = filters.todayOnly ?? false;

  const handleTypeToggle = (type: ChatType) => {
    const newTypes = selectedTypes.includes(type)
      ? selectedTypes.filter(t => t !== type)
      : [...selectedTypes, type];

    // Если ничего не выбрано — выбираем все
    const finalTypes = newTypes.length === 0 ? ['user', 'group', 'channel', 'bot'] as ChatType[] : newTypes;

    onFiltersChange?.({
      chatTypes: finalTypes as ChatType[],
      unreadOnly,
      todayOnly,
    });
  };

  const handleUnreadToggle = () => {
    const newUnreadOnly = !unreadOnly;
    onFiltersChange?.({
      chatTypes: selectedTypes as ChatType[],
      unreadOnly: newUnreadOnly,
      todayOnly,
    });
  };

  const handleTodayToggle = () => {
    const newTodayOnly = !todayOnly;
    onFiltersChange?.({
      chatTypes: selectedTypes as ChatType[],
      unreadOnly,
      todayOnly: newTodayOnly,
    });
  };

  const isFiltering = selectedTypes.length < 4 || unreadOnly || todayOnly;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`h-10 w-10 ${isFiltering ? 'ring-1 ring-amber-400' : ''}`}
          title="Фильтры"
        >
          <Filter className="h-4 w-4" />
          {isFiltering && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-amber-400" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {/* Chat Types */}
        <DropdownMenuLabel>Тип чата</DropdownMenuLabel>
        {CHAT_TYPES.map(({ value, label, icon }) => (
          <DropdownMenuCheckboxItem
            key={value}
            checked={selectedTypes.includes(value)}
            onCheckedChange={() => handleTypeToggle(value)}
            className="flex items-center gap-2"
          >
            <span className="flex items-center gap-2">
              {icon}
              {label}
            </span>
          </DropdownMenuCheckboxItem>
        ))}

        <DropdownMenuSeparator />

        {/* Additional Filters */}
        <DropdownMenuLabel>Содержимое</DropdownMenuLabel>
        <DropdownMenuCheckboxItem
          checked={unreadOnly}
          onCheckedChange={handleUnreadToggle}
        >
          Только непрочитанные
        </DropdownMenuCheckboxItem>

        <DropdownMenuCheckboxItem
          checked={todayOnly}
          onCheckedChange={handleTodayToggle}
        >
          Только сегодняшние
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
